import React, { useEffect, useRef, useState } from 'react'
import useTranslate from 'helpers/hooks/useTranslate'
import {
    DefaultButton, Icon, PrimaryButton, Toggle,
} from '@fluentui/react'
import styles from 'styles/components/containers/cookie-consent-popup.module.scss'

/**
 * CookieConsentPopup
 * @param {object} props Props
 * @param {boolean} props.isVisible Is popup visible
 * @param {object} props.initialPreferences Initial cookie preferences
 * @param {Function} props.onAccept Accept action
 * @param {Function} props.onRefuse Refuse action
 * @param {Function} props.onSavePreferences Save preferences action
 * @param {boolean=} props.isCustomizationOnly Opens the preferences panel without the main banner
 * @param {string=} props.lang Current language
 * @param {Function=} props.onDismiss Dismiss action
 * @returns {JSX.Element} Element
 */
export default function CookieConsentPopup({
    isVisible,
    initialPreferences,
    onAccept,
    onRefuse,
    onSavePreferences,
    onDismiss,
    isCustomizationOnly = false,
    lang,
}) {
    const { tKey } = useTranslate({ lang })
    const [isCustomizationVisible, setIsCustomizationVisible] = useState(false)
    const [preferences, setPreferences] = useState(initialPreferences)
    const wasVisibleRef = useRef(false)

    useEffect(() => {
        if (!isVisible) {
            wasVisibleRef.current = false
            return
        }

        if (wasVisibleRef.current)
            return

        wasVisibleRef.current = true
        setIsCustomizationVisible(isCustomizationOnly)
        setPreferences(initialPreferences)
    }, [initialPreferences, isCustomizationOnly, isVisible])

    const toggleGoogleAnalyticsPreference = () => setPreferences(currentPreferences => ({
        ...currentPreferences,
        googleAnalytics: !currentPreferences.googleAnalytics,
    }))

    if (!isVisible)
        return null

    const isPreferencesVisible = isCustomizationOnly || isCustomizationVisible
    const wrapperClassName = isCustomizationOnly ? [styles.wrapper, styles.modal].join(' ') : styles.wrapper

    return (
        <>
            <div
                className={styles.overlay}
                role="presentation"
                onClick={isCustomizationOnly ? onDismiss : undefined}
            />
            <section
                className={wrapperClassName}
                aria-labelledby="cookie-consent-title"
            >
                <div className={styles.content}>
                    <div className={styles.icon}>
                        <Icon
                            iconName="Shield"
                            aria-hidden
                        />
                    </div>
                    <div className={styles.text}>
                        <h2 id="cookie-consent-title">{tKey('cookieConsentTitle')}</h2>
                        {!isPreferencesVisible ? (
                            <p>{tKey('cookieConsentDescription')}</p>
                        ) : (
                            <>
                                <p>{tKey('cookieConsentCustomizationDescription')}</p>
                                <div className={styles.preferences}>
                                    <div className={styles.preference}>
                                        <div>
                                            <h3>{tKey('cookieConsentEssentialTitle')}</h3>
                                            <p>{tKey('cookieConsentEssentialDescription')}</p>
                                        </div>
                                        <Toggle
                                            checked
                                            disabled
                                            onText={tKey('active')}
                                            offText={tKey('active')}
                                        />
                                    </div>
                                    <div className={styles.preference}>
                                        <div>
                                            <h3>Google Analytics</h3>
                                            <p>{tKey('cookieConsentGoogleAnalyticsDescription')}</p>
                                            <a
                                                className={styles['privacy-link']}
                                                href="https://policies.google.com/privacy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {tKey('cookieConsentPrivacyPolicy')}
                                            </a>
                                        </div>
                                        <Toggle
                                            checked={!!preferences.googleAnalytics}
                                            onText={tKey('yes')}
                                            offText={tKey('no')}
                                            onChange={toggleGoogleAnalyticsPreference}
                                        />
                                    </div>
                                    {/* The logic hotjar has not been implemented yet. */}
                                    {/* <div className={styles.preference}>
                                    <div>
                                        <h3>Hotjar</h3>
                                        <p>{tKey('cookieConsentHotjarDescription')}</p>
                                    </div>
                                    <Toggle
                                        checked={preferences.hotjar}
                                        onText={tKey('yes')}
                                        offText={tKey('no')}
                                        onChange={(_, checked) => setPreferences({
                                            ...preferences,
                                            hotjar: !!checked,
                                        })}
                                    />
                                </div> */}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.actions}>
                    {!isPreferencesVisible ? (
                        <>
                            <DefaultButton
                                text={tKey('cookieConsentCustomize')}
                                onClick={() => setIsCustomizationVisible(true)}
                            />
                            <DefaultButton
                                text={tKey('refuse')}
                                onClick={onRefuse}
                            />
                            <PrimaryButton
                                text={tKey('cookieConsentAccept')}
                                onClick={onAccept}
                            />
                        </>
                    ) : (
                        <>
                            {!isCustomizationOnly && (
                                <DefaultButton
                                    text={tKey('return')}
                                    onClick={() => setIsCustomizationVisible(false)}
                                />
                            )}
                            {/* <DefaultButton
                            text={tKey('refuse')}
                            onClick={onRefuse}
                        /> */}
                            <PrimaryButton
                                text={tKey('save')}
                                onClick={() => onSavePreferences(preferences)}
                            />
                        </>
                    )}
                </div>
            </section>
        </>
    )
}
